﻿import { MILL_1 } from "@/setup/mill-config/mill-config";
import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Section, SectionStand } from "../models/Section";
import type { SectionsExportData } from "../pages/sections/models/SectionExport";

export const upsertSection = async (params: {
	section: Section;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const isM1 = Number(params.defaultAreaId) === MILL_1.id;

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.section.SectionId },
		{ name: "SectionName", value: params.section.SectionName },
		{
			name: "LBsPerFoot",
			value: params.section.LBsPerFoot,
		},
		{ name: "SortOrder", value: params.section.SortOrder },
		{ name: "BilletSize", value: params.section.BilletSize },
		{ name: "Description", value: params.section.Description },
		{ name: "TonsPerHour", value: params.section.TonsPerHour },
		{ name: "TargetTPH", value: params.section.TargetTPH },
		{
			name: "TargetTPHPercentage",
			value: params.section.TargetTPHPercentage,
		},
		{ name: "MillYield", value: params.section.MillYield || null },
		{
			name: "FinishedYield",
			value: params.section.FinishedYield || null,
		},
	];

	if (!isM1) {
		parameters.push({
			name: "SectionFamilyId",
			value: params.section.SectionFamilyId,
		});
	}

	const resp = await apiService.callV2(
		`${isM1 ? "[M1].[UpsertSection]" : "[M2].[UpsertSection]"}`,
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting section");
	}
};

export const deleteSection = async (params: {
	sectionId: number | null;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const isM1 = Number(params.defaultAreaId) === MILL_1.id;

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId },
	];

	const resp = await apiService.callV2(
		`${isM1 ? "[M1].[DeleteSection]" : "[M2].[DeleteSection]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting section");
	}
};

export const getSectionStands = async (
	params: { sectionId?: number | null; defaultAreaId?: string | number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId ?? 0 },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetSectionGaps]" : "[M2].[GetSectionStands]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as SectionStand[];
	} else {
		throw new Error(resp.message || "Error fetching section stands");
	}
};

export const upsertSectionStand = async (params: {
	sectionStand: SectionStand;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const isM1 = Number(params.defaultAreaId) === MILL_1.id;
	const { sectionStand } = params;

	const parameters: spParameter[] = [
		{ name: "SectionId", value: sectionStand.SectionId },
		{ name: "StandId", value: sectionStand.StandId },
		{
			name: "SetpointGap",
			value: sectionStand.SetpointGap ? Number(sectionStand.SetpointGap) : null,
		},
		{ name: "Comments", value: sectionStand.Comments },
	];

	if (!isM1) {
		parameters.push(
			{ name: "StandTypeCode", value: sectionStand.StandTypeCode },
			{
				name: "WidthMeasurement",
				value:
					sectionStand.StandTypeCode === "U"
						? sectionStand.WidthMeasurement === null ||
							sectionStand.WidthMeasurement === ""
							? null
							: Number(sectionStand.WidthMeasurement)
						: null,
			},
			{
				name: "EdgerOrientationCode",
				value:
					sectionStand.StandTypeCode === "E"
						? sectionStand.EdgerOrientationCode
						: null,
			},
			{
				name: "Area",
				value:
					sectionStand.Area === null || sectionStand.Area === ""
						? null
						: Number(sectionStand.Area),
			},
		);
	}

	const resp = await apiService.callV2(
		`${isM1 ? "[M1].[UpsertSectionGap]" : "[M2].[UpsertSectionStand]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error upserting section stand");
	}
};

export const getSectionsExport = async (
	signal?: AbortSignal,
): Promise<SectionsExportData> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2("[SWV].[GetSectionsExport]", [], signal);

	if (resp.ok) {
		return {
			sections: get(resp, "data.tables[0].rows", []),
			stands: get(resp, "data.tables[1].rows", []),
			standSetups: get(resp, "data.tables[2].rows", []),
			specs: get(resp, "data.tables[3].rows", []),
			items: get(resp, "data.tables[4].rows", []),
			specItems: get(resp, "data.tables[5].rows", []),
		};
	} else {
		throw new Error(resp.message || "Error fetching sections export");
	}
};
